/**
 * Karaoke Mugen (kara.moe) API client.
 *
 * The Swagger spec is published at https://api.karaokes.moe/server/swagger.json
 * but the live public API runs at https://kara.moe/api/. CORS is wide-open
 * (access-control-allow-origin: *), so the browser can call it directly —
 * no server-side proxy needed.
 *
 * Reference:
 *   GET /karas/search   — query+filter list of karas
 *   GET /karas/{kid}    — single kara
 *   /downloads/medias/<mediafile>  — direct media (mp3 or mp4)
 *   /downloads/lyrics/<filename>   — .ass lyric files
 *   /karas/{kid}/hardsub           — redirects to the hardsubbed mp4
 */
const API_BASE = 'https://kara.moe/api';
const DOWNLOAD_BASE = 'https://kara.moe/downloads';

export interface KaraTag {
  tid: string;
  name: string;
  short: string | null;
  priority: number;
  type_in_kara: number;
}

export interface KaraLyricsInfo {
  default?: boolean;
  version?: string;
  filename: string;
}

export interface KaraEntry {
  kid: string;
  titles: Record<string, string>;
  titles_default_language: string;
  titles_aliases: string[];
  songorder: number | null;
  year: number | null;
  duration: number;
  mediafile: string;
  hardsubbed_mediafile: string | null;
  mediasize: number;
  repository: string;
  comment: string | null;
  lyrics_infos: KaraLyricsInfo[];
  anilist_ids: string[];
  myanimelist_ids: string[];
  kitsu_ids: string[];
  flag_sensitive_content: boolean;
  songname: string;
  played: number;
  played_recently: number;
  requested: number;
  favorited: number;
  songtypes: KaraTag[];
  langs: KaraTag[];
  versions: KaraTag[];
  series: KaraTag[];
  singers: KaraTag[];
  songwriters: KaraTag[];
  creators: KaraTag[];
  authors: KaraTag[];
  groups?: KaraTag[];
  genres?: KaraTag[];
  families?: KaraTag[];
  origins?: KaraTag[];
  platforms?: KaraTag[];
  misc?: KaraTag[];
}

export interface KaraSearchResponse {
  infos: { count: number; from: number; to: number };
  i18n: Record<string, Record<string, string>>;
  content: KaraEntry[];
}

export interface KaraSearchParams {
  /**
   * Filter expression — see swagger. Separate criteria by `!`:
   *   y:1982!t:<tid>,<tid>!r:kara.moe!eid:anilist,21202
   * Plain text is NOT supported via this field; use tag/EID filters.
   */
  q?: string;
  from?: number;
  size?: number;
  order?:
    | 'recent'
    | 'played'
    | 'playedRecently'
    | 'favorited'
    | 'requested'
    | 'requestedRecently'
    | 'sessionPlayed'
    | 'sessionRequested';
  random?: number;
  signal?: AbortSignal;
}

/**
 * Issue a GET /karas/search against kara.moe.
 *
 * Returns null on network failure — every callsite treats music as
 * non-critical and degrades gracefully.
 */
export async function searchKaras(
  params: KaraSearchParams
): Promise<KaraSearchResponse | null> {
  const usp = new URLSearchParams();
  if (params.q) usp.set('q', params.q);
  if (typeof params.from === 'number') usp.set('from', String(params.from));
  if (typeof params.size === 'number') usp.set('size', String(params.size));
  if (params.order) usp.set('order', params.order);
  if (typeof params.random === 'number')
    usp.set('random', String(params.random));

  try {
    const res = await fetch(`${API_BASE}/karas/search?${usp.toString()}`, {
      headers: { Accept: 'application/json' },
      signal: params.signal,
    });
    if (!res.ok) return null;
    return (await res.json()) as KaraSearchResponse;
  } catch {
    return null;
  }
}

/**
 * Fetch all karas linked to an AniList ID via the `eid:anilist,<id>` filter.
 * Sorted server-side by recency by default — we re-sort client-side by
 * songtype priority (OP → ED → IN → AMV → AUDIO → others) + songorder.
 */
export async function fetchKarasByAnilistId(
  anilistId: number,
  signal?: AbortSignal
): Promise<KaraEntry[]> {
  const data = await searchKaras({
    q: `eid:anilist,${anilistId}`,
    size: 50,
    signal,
  });
  return sortKarasForDisplay(data?.content ?? []);
}

/**
 * Fetch karas by MAL ID — useful when AniList ID is missing.
 */
export async function fetchKarasByMalId(
  malId: number,
  signal?: AbortSignal
): Promise<KaraEntry[]> {
  const data = await searchKaras({
    q: `eid:myanimelist,${malId}`,
    size: 50,
    signal,
  });
  return sortKarasForDisplay(data?.content ?? []);
}

/**
 * Build the direct playable media URL for a kara (mp3 or mp4).
 */
export function getKaraMediaUrl(kara: KaraEntry): string {
  return `${DOWNLOAD_BASE}/medias/${encodeURIComponent(kara.mediafile)}`;
}

/**
 * Hardsubbed mp4 URL (subtitles burnt in). Routed through our
 * `/api/karaokes/hardsub` proxy because kara.moe's `/hardsubs/<file>`
 * endpoint serves the mp4 with CORP / cross-origin headers that block
 * a `<video>` element from playing it across origins. The proxy
 * forwards `Range` both ways and re-emits with same-origin CORS so
 * seek works normally.
 */
export function getKaraHardsubUrl(kara: KaraEntry): string | null {
  if (!kara.hardsubbed_mediafile) return null;
  return `/api/karaokes/hardsub?file=${encodeURIComponent(kara.hardsubbed_mediafile)}`;
}

/**
 * URL of the default .ass lyric file (if any). The `default` flag picks
 * the primary version when there are multiple language variants.
 */
export function getKaraLyricsUrl(kara: KaraEntry): string | null {
  const lyrics = kara.lyrics_infos || [];
  const primary = lyrics.find((info) => info.default) ?? lyrics[0];
  if (!primary?.filename) return null;
  return `${DOWNLOAD_BASE}/lyrics/${encodeURIComponent(primary.filename)}`;
}

/**
 * Pick the best display title from a kara's i18n titles object.
 * Prefers default language → eng → qro (romaji) → first available.
 */
export function pickKaraTitle(kara: KaraEntry): string {
  const titles = kara.titles || {};
  const def = kara.titles_default_language;
  return (
    (def && titles[def]) ||
    titles.eng ||
    titles.qro ||
    titles.jpn ||
    Object.values(titles)[0] ||
    kara.songname ||
    'Untitled'
  );
}

/**
 * Pick the primary songtype name (OP, ED, IN, AMV, AUDIO, COVER…) from
 * a kara. Prefers OP/ED/IN/AMV over generic AUDIO/MV/OTHER when multiple
 * tags are present.
 */
export function pickKaraKind(kara: KaraEntry): string {
  const types = (kara.songtypes || []).map((t) => t.name);
  const preferred = ['OP', 'ED', 'IN', 'AMV', 'OST', 'MV', 'COVER', 'AUDIO'];
  for (const name of preferred) {
    if (types.includes(name)) return name;
  }
  return types[0] || 'AUDIO';
}

const SONGTYPE_PRIORITY: Record<string, number> = {
  OP: 0,
  ED: 1,
  IN: 2,
  AMV: 3,
  OST: 4,
  MV: 5,
  COVER: 6,
  AUDIO: 7,
};

/**
 * Sort karas so OPs come first, then EDs, INs, then by songorder ascending.
 * Stable for equal priorities by falling back to title compare.
 */
export function sortKarasForDisplay(karas: KaraEntry[]): KaraEntry[] {
  return [...karas].sort((a, b) => {
    const kindA = pickKaraKind(a);
    const kindB = pickKaraKind(b);
    const pa = SONGTYPE_PRIORITY[kindA] ?? 99;
    const pb = SONGTYPE_PRIORITY[kindB] ?? 99;
    if (pa !== pb) return pa - pb;

    const sa = a.songorder ?? Number.POSITIVE_INFINITY;
    const sb = b.songorder ?? Number.POSITIVE_INFINITY;
    if (sa !== sb) return sa - sb;

    return pickKaraTitle(a).localeCompare(pickKaraTitle(b));
  });
}

/**
 * Format a duration in seconds as `m:ss`.
 */
export function formatKaraDuration(seconds: number): string {
  if (!Number.isFinite(seconds) || seconds <= 0) return '0:00';
  const total = Math.round(seconds);
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
}

/**
 * Try to derive an AniList ID from a kara so we can deep-link back to
 * the anime page. KM stores external IDs as arrays of strings.
 */
export function pickKaraAnilistId(kara: KaraEntry): number | null {
  const raw = (kara.anilist_ids || [])[0];
  if (!raw) return null;
  const parsed = Number.parseInt(raw, 10);
  return Number.isFinite(parsed) ? parsed : null;
}

/**
 * Surface the primary anime/series name a kara belongs to, falling back
 * to the singers when series is empty (for solo artist tracks).
 */
export function pickKaraAnimeName(kara: KaraEntry): string {
  const series = (kara.series || [])[0]?.name;
  if (series) return series;
  const singer = (kara.singers || [])[0]?.name;
  if (singer) return singer;
  return 'Unknown';
}

/**
 * Returns true when the kara has a video file (mp4/webm) — video karas
 * carry burnt-in lyrics in the hardsub track, so the player can show
 * full-quality karaoke video instead of audio + separate lyrics.
 */
export function isVideoKara(kara: KaraEntry): boolean {
  const f = (kara.mediafile || '').toLowerCase();
  return f.endsWith('.mp4') || f.endsWith('.webm') || f.endsWith('.mkv');
}

/**
 * Fetch the full metadata for a single kara. Used when the music page
 * opens a deep-link view that needs the full tag list.
 */
export async function fetchKaraById(
  kid: string,
  signal?: AbortSignal
): Promise<KaraEntry | null> {
  try {
    const res = await fetch(`${API_BASE}/karas/${encodeURIComponent(kid)}`, {
      headers: { Accept: 'application/json' },
      signal,
    });
    if (!res.ok) return null;
    return (await res.json()) as KaraEntry;
  } catch {
    return null;
  }
}

export interface KaraStats {
  karas: number;
  series: number;
  languages: number;
  singers: number;
  songwriters: number;
  creators: number;
  authors: number;
  duration: number;
  mediasize: string;
}

/**
 * Fetch the global KM database stats — surface count + last-update info
 * in the music page sidebar so users can see the catalogue size.
 */
export async function fetchKaraStats(
  signal?: AbortSignal
): Promise<KaraStats | null> {
  try {
    const res = await fetch(`${API_BASE}/karas/stats`, {
      headers: { Accept: 'application/json' },
      signal,
    });
    if (!res.ok) return null;
    return (await res.json()) as KaraStats;
  } catch {
    return null;
  }
}

/**
 * Most-recent server-side database update timestamp. Returned as an ISO
 * string; null on failure.
 */
export async function fetchKaraLastUpdate(
  signal?: AbortSignal
): Promise<string | null> {
  try {
    const res = await fetch(`${API_BASE}/karas/lastUpdate`, { signal });
    if (!res.ok) return null;
    const text = (await res.text()).trim();
    return text || null;
  } catch {
    return null;
  }
}

export interface ParsedLyricLine {
  start: number;
  end: number;
  text: string;
}

/**
 * Convert an ASS `Dialogue:` timestamp ("h:mm:ss.cc") into seconds.
 */
function parseAssTimestamp(value: string): number {
  const m = value.trim().match(/^(\d+):(\d+):(\d+)\.(\d+)$/);
  if (!m) return 0;
  const [, hours, minutes, seconds, centi] = m;
  return (
    Number.parseInt(hours, 10) * 3600 +
    Number.parseInt(minutes, 10) * 60 +
    Number.parseInt(seconds, 10) +
    Number.parseInt(centi, 10) / 100
  );
}

/**
 * Minimal ASS subtitle parser — pulls Dialogue lines, strips override
 * tags (`{\k12}`, `{\\fad(...)}`, etc.) and returns a deduplicated list
 * of `{ start, end, text }` lyric lines sorted by start time. Furigana /
 * duo-voice variants that share a start timestamp are collapsed to the
 * longest line so the rendered lyrics aren't visually doubled.
 */
export function parseAssLyrics(raw: string): ParsedLyricLine[] {
  const events: ParsedLyricLine[] = [];
  const seen = new Map<number, ParsedLyricLine>();

  raw.split(/\r?\n/).forEach((line) => {
    if (!line.startsWith('Dialogue:')) return;
    const fields = line.slice('Dialogue:'.length).split(',');
    if (fields.length < 10) return;

    const start = parseAssTimestamp(fields[1]);
    const end = parseAssTimestamp(fields[2]);
    if (end <= start) return;

    const rawText = fields.slice(9).join(',');
    const plain = rawText
      .replace(/\{[^}]*\}/g, '')
      .replace(/\\N/g, '\n')
      .replace(/\\h/g, ' ')
      .trim();

    if (!plain) return;

    const existing = seen.get(start);
    if (existing && existing.text.length >= plain.length) return;
    seen.set(start, { start, end, text: plain });
  });

  seen.forEach((line) => events.push(line));
  return events.sort((a, b) => a.start - b.start);
}

/**
 * Fetch + parse the lyrics for a kara. Goes through the server proxy at
 * `/api/karaokes/lyrics` because kara.moe doesn't ship CORS headers on
 * the static download paths.
 */
export async function fetchKaraLyrics(
  kara: KaraEntry,
  signal?: AbortSignal
): Promise<ParsedLyricLine[]> {
  const lyrics = kara.lyrics_infos || [];
  const primary = lyrics.find((l) => l.default) ?? lyrics[0];
  if (!primary?.filename) return [];

  try {
    const res = await fetch(
      `/api/karaokes/lyrics?file=${encodeURIComponent(primary.filename)}`,
      { signal }
    );
    if (!res.ok) return [];
    const text = await res.text();
    return parseAssLyrics(text);
  } catch {
    return [];
  }
}

/**
 * Direct link to the kara's page on kara.moe — useful as a "view source"
 * affordance on detail panels.
 */
export function getKaraExternalUrl(kara: KaraEntry): string {
  return `https://kara.moe/kara/${kara.kid}`;
}

export interface KaraokeSyllable {
  text: string;
  /** Offset from the line start, in milliseconds. */
  startMs: number;
  /** Duration of this syllable in milliseconds (0 = static, no fill). */
  durationMs: number;
}

export interface KaraokeLine {
  /** Absolute line start, in milliseconds. */
  startMs: number;
  endMs: number;
  text: string;
  syllables: KaraokeSyllable[];
}

/**
 * Karaoke-aware ASS parser. Walks each `Dialogue:` line's text field,
 * recognising `{\k<n>}` override tags (n = duration in centiseconds for
 * the following syllable). Each line becomes a list of syllables with
 * per-syllable timing offsets so a UI can render progressive word-by-
 * word fill highlighting in sync with `audio.currentTime`.
 *
 * Dedupes furigana / duo-voice variants that share a start timestamp by
 * keeping the line with the most syllables (or longest text).
 */
export function parseAssKaraoke(raw: string): KaraokeLine[] {
  const candidates: KaraokeLine[] = [];

  raw.split(/\r?\n/).forEach((rawLine) => {
    if (!rawLine.startsWith('Dialogue:')) return;
    const fields = rawLine.slice('Dialogue:'.length).split(',');
    if (fields.length < 10) return;

    const startSec = parseAssTimestamp(fields[1]);
    const endSec = parseAssTimestamp(fields[2]);
    if (endSec <= startSec) return;

    const rawText = fields.slice(9).join(',');

    // Tokenize into alternating { override } and plain-text chunks.
    interface Token {
      kind: 'override' | 'text';
      value: string;
    }
    const tokens: Token[] = [];
    const overrideRe = /\{([^}]*)\}/g;
    let cursor = 0;
    let match: RegExpExecArray | null = overrideRe.exec(rawText);
    while (match !== null) {
      if (match.index > cursor) {
        tokens.push({ kind: 'text', value: rawText.slice(cursor, match.index) });
      }
      tokens.push({ kind: 'override', value: match[1] });
      cursor = match.index + match[0].length;
      match = overrideRe.exec(rawText);
    }
    if (cursor < rawText.length) {
      tokens.push({ kind: 'text', value: rawText.slice(cursor) });
    }

    // Walk tokens — each `\k<n>` sets the duration for the NEXT plain
    // text segment.
    const syllables: KaraokeSyllable[] = [];
    let offsetMs = 0;
    let pendingDuration: number | null = null;

    tokens.forEach((tok) => {
      if (tok.kind === 'override') {
        // \k, \K, \kf and \ko all describe per-syllable timing in
        // centiseconds — KM only uses \k, but be permissive.
        const k = tok.value.match(/\\[kK][of]?(\d+)/);
        if (k) pendingDuration = Number.parseInt(k[1], 10) * 10;
        return;
      }

      const text = tok.value.replace(/\\N/g, '\n').replace(/\\h/g, ' ');
      if (!text) return;

      if (pendingDuration !== null) {
        syllables.push({
          text,
          startMs: offsetMs,
          durationMs: pendingDuration,
        });
        offsetMs += pendingDuration;
        pendingDuration = null;
      } else if (syllables.length > 0) {
        // Continuation text after a syllable (rare) — append to the
        // previous syllable so it stays grouped.
        syllables[syllables.length - 1].text += text;
      } else {
        // Plain dialogue with no \k tags — static line with no fill.
        syllables.push({ text, startMs: 0, durationMs: 0 });
      }
    });

    const fullText = syllables
      .map((s) => s.text)
      .join('')
      .trim();
    if (!fullText) return;

    candidates.push({
      startMs: Math.round(startSec * 1000),
      endMs: Math.round(endSec * 1000),
      text: fullText,
      syllables,
    });
  });

  // Dedupe lines sharing a start timestamp — keep the variant with the
  // most syllables (or longest text on ties). Furigana / duo-voice
  // variants get filtered out so the lyrics aren't visually doubled.
  const bestAt = new Map<number, KaraokeLine>();
  candidates.forEach((line) => {
    const existing = bestAt.get(line.startMs);
    if (!existing) {
      bestAt.set(line.startMs, line);
      return;
    }
    if (line.syllables.length > existing.syllables.length) {
      bestAt.set(line.startMs, line);
    } else if (
      line.syllables.length === existing.syllables.length &&
      line.text.length > existing.text.length
    ) {
      bestAt.set(line.startMs, line);
    }
  });

  return [...bestAt.values()].sort((a, b) => a.startMs - b.startMs);
}

/**
 * Returns the index of the active line in a karaoke-parsed lyrics list
 * given the current playback position in milliseconds. -1 when nothing
 * matches (intro / outro silence).
 */
export function findActiveKaraokeLine(
  lines: KaraokeLine[],
  currentMs: number
): number {
  if (lines.length === 0) return -1;
  // Binary search would be overkill — lyric lines are O(100). Linear
  // scan is plenty fast and stays simple.
  for (let i = 0; i < lines.length; i += 1) {
    const line = lines[i];
    if (currentMs >= line.startMs && currentMs < line.endMs) return i;
  }
  // Outside any window — show the last passed line until the next one
  // arrives, so the viewer still has lyric context to look at.
  let last = -1;
  for (let i = 0; i < lines.length; i += 1) {
    if (lines[i].startMs <= currentMs) last = i;
    else break;
  }
  return last;
}

/**
 * Karaoke variant of fetchKaraLyrics — returns syllable-timed lines for
 * the karaoke fill effect. Falls back to a plain-line shape when the
 * source `.ass` doesn't carry `\k` timing tags.
 */
export async function fetchKaraokeLyrics(
  kara: KaraEntry,
  signal?: AbortSignal
): Promise<KaraokeLine[]> {
  const lyrics = kara.lyrics_infos || [];
  const primary = lyrics.find((l) => l.default) ?? lyrics[0];
  if (!primary?.filename) return [];

  try {
    const res = await fetch(
      `/api/karaokes/lyrics?file=${encodeURIComponent(primary.filename)}`,
      { signal }
    );
    if (!res.ok) return [];
    const text = await res.text();
    return parseAssKaraoke(text);
  } catch {
    return [];
  }
}
